import type { ReactNode } from 'react';
import Avatar from '@components/ui/Avatar/Avatar';
import ProfileImageEditor from '@components/shared/ProfileImageEditor';
import styles from './ProfileCard.module.scss';

export interface ProfileField {
  label: string;
  value: string;
}

export interface EditableAvatarConfig {
  currentImageUrl: string | null;
  canEdit: boolean;
  onSave: (blob: Blob) => Promise<void>;
  onRemove?: () => Promise<void>;
  captureMode?: 'user' | 'environment';
}

interface ProfileCardProps {
  name: string;
  fields: ProfileField[];
  avatarColor?: 'red' | 'blue' | 'green' | 'purple';
  loading?: boolean;
  action?: ReactNode;
  /** Number of columns for the fields grid (default: 1) */
  columns?: 1 | 2;
  /** When provided, the avatar becomes editable. Consumer owns the save/remove handlers. */
  editableAvatar?: EditableAvatarConfig;
}

function ProfileCard({
  name,
  fields,
  avatarColor = 'red',
  loading,
  action,
  columns = 1,
  editableAvatar,
}: ProfileCardProps) {
  if (loading) {
    return (
      <div className={styles.profileCard}>
        <div className={`${styles.profileContent} ${styles.skeleton}`}>
          <div className={styles.avatarColumn}>
            <Avatar name="" size="xl" color={avatarColor} />
          </div>
          <div className={styles.profileInfo}>
            <div className={styles.profileName} />
            <dl className={`${styles.infoGrid} ${columns === 2 ? styles.infoGridTwoCol : ''}`}>
              {Array.from({ length: 8 }, (_, i) => (
                <div key={i} className={styles.infoRow}>
                  <dt>&nbsp;</dt>
                  <dd><span className={styles.skeletonValue} /></dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.profileCard}>
      {action && <div className={styles.actionRow}>{action}</div>}
      <div className={styles.profileContent}>
        <div className={styles.avatarColumn}>
          {editableAvatar ? (
            <ProfileImageEditor
              displayName={name}
              size="xl"
              color={avatarColor}
              currentImageUrl={editableAvatar.currentImageUrl}
              canEdit={editableAvatar.canEdit}
              onSave={editableAvatar.onSave}
              {...(editableAvatar.onRemove ? { onRemove: editableAvatar.onRemove } : {})}
              {...(editableAvatar.captureMode ? { captureMode: editableAvatar.captureMode } : {})}
            />
          ) : (
            <Avatar name={name} size="xl" color={avatarColor} />
          )}
        </div>
        <div className={styles.profileInfo}>
          <h2 className={styles.profileName}>{name}</h2>
          <dl className={`${styles.infoGrid} ${columns === 2 ? styles.infoGridTwoCol : ''}`}>
            {fields.map((field) => (
              <div key={field.label} className={styles.infoRow}>
                <dt>{field.label}:</dt>
                <dd>{field.value || '-'}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

export default ProfileCard;
